import { apply, Dictionary, toPairs } from 'ramda'
import { Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router'
import store from 'app/store'
import { memoize, pathJoin } from 'app/utils/misc'
import Progress from 'core/components/progress/Progress'
import ForbiddenPage from 'core/public/ForbiddenPage'
import { permissionsKey } from 'core/session/permissionsReducers'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { hasPermissions } from 'openstack/helpers'
import { ensureArray, isNilOrEmpty } from 'utils/fp'
import { renderDevTools, renderPublicPages } from './base-views'
import { PluginSection } from './model'
import Plugin from './plugin'

export const parseNavItem = (basePath) => (navItem) => {
  // Handle undefined or invalid items gracefully
  if (!navItem || !navItem.link) {
    return navItem
  }
  return {
    ...navItem,
    link: {
      ...navItem.link,
      path: navItem.link.external ? navItem.link.path : pathJoin(basePath, navItem.link.path),
      navPath: navItem.link.navPath ? pathJoin(basePath, navItem.link.navPath) : null,
      definition: navItem.link.definition ? pathJoin(basePath, navItem.link.definition) : null,
      defaultPath: navItem.link.defaultPath ? pathJoin(basePath, navItem.link.defaultPath) : null,
    },
    nestedLinks: navItem.nestedLinks ? navItem.nestedLinks.map(parseNavItem(basePath)) : null,
  }
}

const defaultOptions = {
  showFooter: false,
  showNavMenu: true,
  showSidebar: false,
}

export const initData = (frame = null) => ({
  frame,
  components: [],
  routes: [],
  navItems: [],
  secondaryHeader: null,
  options: { ...defaultOptions },
})

export const renderPluginRoutes = (role) => (id, plugin) => {
  const defaultRoute = plugin.getDefaultRoute()
  const genericRoutes = [
    {
      link: { path: pathJoin(plugin.basePath, '') },
      // TODO: Implement 404 page
      render: () => <Redirect to={defaultRoute || '/ui/404'} />,
    },
  ]
  const features = store.getState()[sessionStoreKey]?.features
  const validRoutes = plugin.getRoutes().filter((route) => {
    // First, filter out null, undefined, or routes without link property
    if (!route || !route.link) {
      return false
    }
    // Filter out routes that have neither component nor render
    if (!route.component && !route.render) {
      return false
    }
    return true
  })

  // `loaded` is only false between login and the first permissions fetch resolving.
  const permissionsLoaded = store.getState()[permissionsKey]?.loaded !== false

  const passesRoleAndFeatureChecks = (route) => {
    const { requiredRoles, bannedRoles, requiredFeatures } = route
    if (!isNilOrEmpty(requiredRoles) && !ensureArray(requiredRoles).includes(role)) return false
    if (!isNilOrEmpty(bannedRoles) && ensureArray(bannedRoles).includes(role)) return false
    if (!isNilOrEmpty(requiredFeatures) && !requiredFeatures(features)) return false
    return true
  }

  const passesPermissionCheck = ({ requiredPermissions }) =>
    isNilOrEmpty(requiredPermissions) ||
    requiredPermissions.some((permission) => hasPermissions(permission))

  return [...validRoutes, ...genericRoutes].map((route) => {
    const { component: Component, render, link } = route

    // Unauthorized routes must still claim their exact path in the Switch so a
    // broader sibling route (e.g. a details page with an :id param) doesn't
    // silently swallow the URL instead. Render ForbiddenPage in place rather
    // than redirecting, so the URL (and browser history) still reflects the
    // resource the user actually tried to reach.
    // A permission-based denial is only final once permissions have loaded; until then show
    // a loading state so a permitted custom-role user doesn't see a flash of Access Denied.
    if (!passesRoleAndFeatureChecks(route) || !passesPermissionCheck(route)) {
      const pending = passesRoleAndFeatureChecks(route) && !permissionsLoaded
      return (
        <Route
          key={link.path}
          path={link.path}
          exact={link.exact || false}
          render={() => (pending ? <Progress loading message="Loading..." /> : <ForbiddenPage />)}
        />
      )
    }

    // If there's a component, wrap it with Suspense for lazy loading support
    if (Component) {
      return (
        <Route
          key={link.path}
          path={link.path}
          exact={link.exact || false}
          render={(props) => (
            <Suspense fallback={<Progress loading message="Loading..." />}>
              <Component {...props} />
            </Suspense>
          )}
        />
      )
    }

    // Otherwise use the render prop directly
    return <Route key={link.path} path={link.path} exact={link.exact || false} render={render} />
  })
}

// `permissions` is accepted only so it's part of the memoization key below — hasPermissions()
// (called inside renderPluginRoutes via isAuthorized) always reads the live value from the
// store itself. Without this param, a tenant switch (which dispatches updatePermissions but
// leaves `plugins`/`role` unchanged) would return the stale, memoized Route array instead of
// recomputing requiredPermissions-guarded routes for the new tenant.
export const renderPlugins = memoize((plugins, role, _permissions) =>
  toPairs(plugins).flatMap(apply(renderPluginRoutes(role))),
)

export const renderPluginComponents = (id, plugin) => {
  const pluginComponents = plugin.getComponents()

  return (
    <Route key={plugin.basePath} path={plugin.basePath} exact={false}>
      {pluginComponents.map((PluginComponent, idx) => (
        <PluginComponent key={idx} />
      ))}
    </Route>
  )
}

export const renderRawComponents = memoize((plugins) =>
  toPairs(plugins).flatMap(apply(renderPluginComponents)),
)

// `permissions` must be passed explicitly by the caller and threaded through as an argument
// (not read from the store in here) so it's part of moize's cache key — same reason as
// `renderPlugins`/`renderMainContent` below: a tenant switch changes state.permissions without
// changing plugins/role/features, and the requiredPermissions filter reads permissions live via
// hasPermissions(), so without this the memoized sections would go stale after a tenant switch.
export const getSections = memoize(
  (plugins: Dictionary<Plugin>, role, features, _permissions): PluginSection[] =>
    toPairs(plugins).map(([id, plugin]) => ({
      id,
      name: plugin.name,
      icon: plugin.icon,
      description: plugin.description,
      links: plugin
        .getNavItems()
        .filter(
          ({ requiredRoles }) =>
            isNilOrEmpty(requiredRoles) || ensureArray(requiredRoles).includes(role),
        )
        .filter(
          ({ bannedRoles }) =>
            isNilOrEmpty(bannedRoles) || !ensureArray(bannedRoles).includes(role),
        )
        .filter(
          ({ requiredFeatures }) => isNilOrEmpty(requiredFeatures) || requiredFeatures(features),
        )
        .filter(
          ({ requiredPermissions }) =>
            isNilOrEmpty(requiredPermissions) ||
            requiredPermissions.some((permission) => hasPermissions(permission)),
        ),
    })),
)

// `permissions` must be passed explicitly by the caller (not defaulted/read from the store in
// here) so it's part of moize's cache key based on the actual arguments passed at the call
// site — a default value evaluated inside the function body wouldn't affect that key.
export const renderMainContent = memoize((plugins, role, isDevEnabled, permissions) => (
  <>
    {renderRawComponents(plugins)}
    <Switch>
      {renderPlugins(plugins, role, permissions)}
      {renderPublicPages()}
    </Switch>
    {isDevEnabled && renderDevTools()}
  </>
))
